// Detects a newer deploy (dist/version.json, emitted by vue.config.js) so the router can swap
// versions on the next page change instead of reloading open tabs mid-work.
const CURRENT_BUILD_ID = process.env.VUE_APP_BUILD_ID
const CHECK_THROTTLE_MS = 60 * 1000
const CHECK_INTERVAL_MS = 5 * 60 * 1000
const RELOAD_GUARD_KEY = 'mashup_chunk_reload_at'
const RELOAD_GUARD_MS = 60 * 1000

let updatePending = false
let lastCheckAt = 0

export function isUpdatePending() {
  return updatePending
}

// Work a reload would lose (e.g. RequestManager rows in flight): the version swap waits for a later navigation
const reloadBlockers = new Set()
export function addReloadBlocker(isBlocking) {
  reloadBlockers.add(isBlocking)
  return () => reloadBlockers.delete(isBlocking)
}

export function isReloadBlocked() {
  return [...reloadBlockers].some((isBlocking) => isBlocking())
}

export function checkForUpdate() {
  if (!CURRENT_BUILD_ID || updatePending || process.env.NODE_ENV === 'local') return
  if (Date.now() - lastCheckAt < CHECK_THROTTLE_MS) return
  lastCheckAt = Date.now()

  fetch(`${process.env.BASE_URL}version.json`, { cache: 'no-store' })
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      if (data && data.buildId && data.buildId !== CURRENT_BUILD_ID) updatePending = true
    })
    .catch(() => {})
}

export function startUpdateWatcher() {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkForUpdate()
  })
  setInterval(checkForUpdate, CHECK_INTERVAL_MS)
}

export function isStaleChunkError(error) {
  if (!error) return false
  const message = error.message || ''
  return /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message) ||
    /ChunkLoadError/i.test(error.name || '') ||
    /Cannot read propert(y|ies) of undefined \(reading 'call'\)/i.test(message)
}

// Returns false (caller should surface the error) when a reload already happened less than a minute ago:
// the chunk is genuinely broken, not just stale, and reloading again would loop.
export function recoverFromStaleChunk(targetHref, beforeReload) {
  let lastReloadAt = 0
  try { lastReloadAt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0 } catch (e) { /* storage unavailable */ }
  if (Date.now() - lastReloadAt < RELOAD_GUARD_MS) return false

  try { sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now())) } catch (e) { /* storage unavailable */ }
  if (beforeReload) beforeReload()
  reloadAt(targetHref)
  return true
}

// Set by the router (it owns the postCreator draft-save logic); kept as a callback to avoid a component → router import
let staleChunkHandler = null
export function setStaleChunkHandler(handler) {
  staleChunkHandler = handler
}

// Vue 2 swallows async component load failures (no errorHandler call), so lazy components go through this wrapper
export function lazyComponent(loader) {
  return () => loader().catch((error) => {
    if (isStaleChunkError(error) && staleChunkHandler) staleChunkHandler(error)
    throw error
  })
}

// Hash-mode router: location.assign('#/x') would only change the hash, so push the target then reload
export function reloadAt(targetHref) {
  if (targetHref && targetHref !== window.location.hash) window.history.pushState(null, '', targetHref)
  window.location.reload()
}

// True if semver a (x.y.z) is strictly greater than b
export function isNewerVersion(a, b) {
  const pa = String(a).split('.').map(v => parseInt(v) || 0)
  const pb = String(b).split('.').map(v => parseInt(v) || 0)
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    if ((pa[i] || 0) !== (pb[i] || 0)) return (pa[i] || 0) > (pb[i] || 0)
  }
  return false
}
